/* Orbytag Satellite theme scripts. No dependencies. */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  /* ------------------------------------------------------------------ utilities */
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };
  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');
  const fetchText = (url, opts = {}) =>
    fetch(url, opts).then((r) => {
      if (!r.ok) throw new Error(r.status);
      return r.text();
    });
  const rootUrl = (path) => {
    const root = routes.root || '/';
    return (root.endsWith('/') ? root : root + '/') + path.replace(/^\//, '');
  };
  const motionAllowed = () => theme.motion !== false && !reducedMotion.matches;

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    format = format || theme.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    };
    const match = format.match(placeholder);
    if (!match) return format;
    let value;
    switch (match[1]) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  /* Toast */
  let toastTimer;
  function toast(message) {
    const el = qs('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }
  theme.toast = toast;

  /* Focus trap */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';
  const trap = { container: null, handler: null };
  function trapFocus(container, initial) {
    releaseFocus();
    trap.container = container;
    trap.handler = (event) => {
      if (event.key !== 'Tab') return;
      const items = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trap.handler);
    requestAnimationFrame(() => (initial || container).focus({ preventScroll: true }));
  }
  function releaseFocus() {
    if (trap.handler) document.removeEventListener('keydown', trap.handler);
    trap.container = null;
    trap.handler = null;
  }

  /* Drawers: a panel plus its own overlay */
  const openStack = [];
  function openDrawer(panel, overlay, trigger) {
    if (!panel) return;
    panel.classList.add('is-open');
    if (overlay) overlay.classList.add('is-visible');
    document.body.classList.add('has-overlay');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    openStack.push({ panel, overlay, trigger });
    trapFocus(panel);
  }
  function closeDrawer(panel) {
    const index = openStack.findIndex((entry) => entry.panel === panel);
    if (index === -1) return;
    const { overlay, trigger } = openStack[index];
    openStack.splice(index, 1);
    panel.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-visible');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus({ preventScroll: true });
    }
    releaseFocus();
    if (!openStack.length) document.body.classList.remove('has-overlay');
    else trapFocus(openStack[openStack.length - 1].panel);
  }
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openStack.length) {
      closeDrawer(openStack[openStack.length - 1].panel);
    }
  });

  /* ------------------------------------------------------------------ cart state */
  const cart = {
    sectionsFor() {
      const ids = ['cart-drawer'];
      const page = qs('cart-page');
      if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
      return ids;
    },
    updateCount(count) {
      qsa('[data-cart-count]').forEach((el) => (el.textContent = count > 0 ? count : ''));
      qsa('[data-cart-count-label]').forEach((el) => (el.textContent = String(count)));
    },
    renderSections(sections) {
      if (!sections) return;
      const drawer = qs('cart-drawer');
      if (drawer && sections['cart-drawer']) drawer.render(sections['cart-drawer']);
      const page = qs('cart-page');
      if (page && sections[page.dataset.sectionId]) page.render(sections[page.dataset.sectionId]);
    },
    async refresh(open = false) {
      const ids = this.sectionsFor().join(',');
      const [data, state] = await Promise.all([
        fetch(`${window.location.pathname}?sections=${ids}`).then((r) => r.json()),
        fetch(rootUrl('cart.js')).then((r) => r.json()),
      ]);
      this.renderSections(data);
      this.updateCount(state.item_count);
      if (open) this.open();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: state }));
      return state;
    },
    open() {
      const drawer = qs('cart-drawer');
      if (theme.cartType === 'drawer' && drawer) drawer.open();
      else window.location.href = routes.cart;
    },
    async add(formData) {
      formData.append('sections', this.sectionsFor().join(','));
      formData.append('sections_url', window.location.pathname);
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || strings.error);
        error.data = data;
        throw error;
      }
      this.renderSections(data.sections);
      const state = await fetch(rootUrl('cart.js')).then((r) => r.json());
      this.updateCount(state.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: state }));
      return data;
    },
    async change(line, quantity) {
      const response = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionsFor(), sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || strings.error);
        error.data = data;
        throw error;
      }
      this.renderSections(data.sections);
      this.updateCount(data.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },
    note: debounce((note) => {
      fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    }, 500),
  };
  theme.cart = cart;

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      fetch(rootUrl('cart.js'))
        .then((r) => r.json())
        .then((state) => cart.updateCount(state.item_count))
        .catch(() => {});
    }
  });

  /* ------------------------------------------------------------------ header */
  class HeaderShell extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.section-header') || this;
      this.header = qs('[data-header]', this);
      this.mode = this.dataset.sticky || 'always';
      if (this.mode === 'none') this.wrapper.classList.add('is-static');

      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      this.resizeObserver = new ResizeObserver(() => this.setHeight());
      this.resizeObserver.observe(this.wrapper);
      this.setHeight();

      this.setupMega();

      const menuButton = qs('[data-menu-open]', this);
      if (menuButton) {
        menuButton.addEventListener('click', () => {
          const drawer = qs('menu-drawer');
          if (drawer) drawer.open(menuButton);
        });
      }
      qsa('[data-cart-toggle]', this).forEach((link) => {
        link.addEventListener('click', (event) => {
          if (theme.cartType === 'drawer' && qs('cart-drawer')) {
            event.preventDefault();
            qs('cart-drawer').open(link);
          }
        });
      });
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
    setHeight() {
      const announce = qs('.section-announcement');
      const height = this.wrapper.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-h', `${Math.round(height)}px`);
      if (announce) document.documentElement.style.setProperty('--announce-h', `${Math.round(announce.getBoundingClientRect().height)}px`);
    }
    onScroll() {
      const y = window.scrollY;
      if (this.header) this.header.classList.toggle('is-scrolled', y > 4);
      if (this.mode === 'scroll-up') {
        const goingDown = y > this.lastY && y > 200;
        this.wrapper.classList.toggle('is-hidden', goingDown && !this.header.classList.contains('is-open'));
      }
      this.lastY = y;
    }
    setupMega() {
      const items = qsa('[data-nav-item].has-mega', this);
      items.forEach((item) => {
        const link = qs('.nav__link', item);
        let openTimer;
        let closeTimer;
        const open = () => {
          clearTimeout(closeTimer);
          openTimer = setTimeout(() => {
            items.forEach((other) => other !== item && this.closeItem(other));
            item.classList.add('is-open');
            link.setAttribute('aria-expanded', 'true');
            this.header.classList.add('is-open');
          }, 70);
        };
        const close = () => {
          clearTimeout(openTimer);
          closeTimer = setTimeout(() => this.closeItem(item), 140);
        };
        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', close);
        item.addEventListener('focusin', open);
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) close();
        });
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && item.classList.contains('is-open')) {
            event.stopPropagation();
            this.closeItem(item);
            link.focus();
          }
        });
      });
    }
    closeItem(item) {
      item.classList.remove('is-open');
      const link = qs('.nav__link', item);
      if (link) link.setAttribute('aria-expanded', 'false');
      if (!qs('[data-nav-item].is-open', this)) this.header.classList.remove('is-open');
    }
  }
  customElements.define('header-shell', HeaderShell);

  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = qs('.drawer', this);
      this.overlay = qs('[data-drawer-overlay]', this);
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      qsa('[data-close]', this).forEach((btn) => btn.addEventListener('click', () => this.close()));
      qsa('[data-accordion-toggle]', this).forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const target = document.getElementById(toggle.getAttribute('aria-controls'));
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!expanded));
          if (target) target.hidden = expanded;
        });
      });
    }
    open(trigger) {
      openDrawer(this.panel, this.overlay, trigger);
    }
    close() {
      closeDrawer(this.panel);
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* ------------------------------------------------------------------ predictive search */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      this.form = qs('form', this);
      this.cache = new Map();
      if (!this.input || this.dataset.enabled === 'false') return;

      this.input.addEventListener('input', debounce(() => this.onInput(), 220));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length > 1 && this.results.innerHTML.trim()) this.show();
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      const reset = qs('[data-search-reset]', this);
      if (reset) {
        reset.addEventListener('click', () => {
          this.input.value = '';
          this.hide();
          this.input.focus();
        });
      }
      this.onDocClick = (event) => {
        if (!this.contains(event.target)) this.hide();
      };
      document.addEventListener('click', this.onDocClick);
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }
    onInput() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.hide();
        return;
      }
      this.fetchResults(query);
    }
    async fetchResults(query) {
      if (this.cache.has(query)) {
        this.render(this.cache.get(query));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.results.innerHTML = '<div class="predictive__loading"><span class="orbit-spinner" aria-hidden="true"></span></div>';
      this.show();
      const params = new URLSearchParams({
        q: query,
        section_id: 'predictive-search',
        'resources[type]': 'query,collection,product,page',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const text = await fetchText(`${routes.predictiveSearch}?${params.toString()}`, { signal: this.controller.signal });
        const content = qs('[data-predictive-content]', parseHTML(text));
        const html = content ? content.innerHTML : '';
        this.cache.set(query, html);
        this.render(html);
      } catch (error) {
        if (error.name !== 'AbortError') this.hide();
      }
    }
    render(html) {
      this.results.innerHTML = html || `<p class="predictive__empty">${strings.searchEmpty || ''}</p>`;
      this.show();
    }
    show() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }
    hide() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      qsa('[aria-selected="true"]', this.results).forEach((el) => el.setAttribute('aria-selected', 'false'));
    }
    onKeydown(event) {
      if (this.results.hidden) return;
      const options = qsa('[role="option"]', this.results);
      const current = options.findIndex((el) => el.getAttribute('aria-selected') === 'true');
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!options.length) return;
        let next = event.key === 'ArrowDown' ? current + 1 : current - 1;
        if (next < 0) next = options.length - 1;
        if (next >= options.length) next = 0;
        options.forEach((el) => el.setAttribute('aria-selected', 'false'));
        options[next].setAttribute('aria-selected', 'true');
        options[next].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && current > -1) {
        event.preventDefault();
        window.location.href = options[current].href;
      } else if (event.key === 'Escape') {
        event.stopPropagation();
        this.hide();
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------ announcement */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = qsa('.announce__item', this);
      if (this.items.length < 2) return;
      this.index = 0;
      this.interval = (Number(this.dataset.interval) || 5) * 1000;
      this.paused = false;
      ['mouseenter', 'focusin'].forEach((e) => this.addEventListener(e, () => (this.paused = true)));
      ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => (this.paused = false)));
      this.timer = setInterval(() => {
        if (this.paused || document.hidden) return;
        this.items[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].classList.add('is-active');
      }, this.interval);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ------------------------------------------------------------------ orbit hero */
  class OrbitHero extends HTMLElement {
    connectedCallback() {
      this.stage = qs('[data-orbit-stage]', this);
      this.nodes = qsa('[data-orbit-node]', this);
      this.sat = qs('[data-orbit-sat]', this);
      this.pauseButton = qs('[data-orbit-pause]', this);
      if (!this.stage || !this.nodes.length) return;

      this.speed = Number(this.dataset.speed) || 110;
      this.wantsMotion = this.dataset.motion !== 'false';
      this.theta = 0;
      this.hoverPause = false;
      this.userPause = false;
      this.visible = true;
      this.last = 0;
      this.classList.add('is-live');

      this.measure();
      this.draw();

      this.resizeObserver = new ResizeObserver(() => {
        this.measure();
        this.draw();
      });
      this.resizeObserver.observe(this.stage);

      const ring = qs('.orbit__ring', this);
      ['mouseenter', 'focusin'].forEach((e) => ring.addEventListener(e, () => (this.hoverPause = true)));
      ['mouseleave', 'focusout'].forEach((e) =>
        ring.addEventListener(e, (event) => {
          if (e === 'focusout' && ring.contains(event.relatedTarget)) return;
          this.hoverPause = false;
        })
      );

      if (this.pauseButton) {
        if (!motionAllowed() || !this.wantsMotion) this.pauseButton.hidden = true;
        this.pauseButton.addEventListener('click', () => {
          this.userPause = !this.userPause;
          this.pauseButton.setAttribute('aria-pressed', String(this.userPause));
          qs('[data-label-pause]', this.pauseButton).hidden = this.userPause;
          qs('[data-label-play]', this.pauseButton).hidden = !this.userPause;
        });
      }

      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
      });
      this.io.observe(this);

      this.setupDrag();

      this.frame = this.frame.bind(this);
      this.raf = requestAnimationFrame(this.frame);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.io) this.io.disconnect();
    }
    measure() {
      const rect = this.stage.getBoundingClientRect();
      const styles = getComputedStyle(this);
      const orx = parseFloat(styles.getPropertyValue('--orx')) || 0.44;
      const ory = parseFloat(styles.getPropertyValue('--ory')) || 0.34;
      this.w = rect.width;
      this.h = rect.height;
      this.cx = this.w / 2;
      this.cy = this.h / 2;
      this.rx = this.w * orx;
      this.ry = this.h * ory;
      this.desktop = isDesktop();
      const ellipse = qs('[data-orbit-ellipse]', this);
      const inner = qs('[data-orbit-inner]', this);
      if (ellipse) {
        ellipse.setAttribute('rx', (orx * 100).toFixed(2));
        ellipse.setAttribute('ry', (ory * 100).toFixed(2));
      }
      if (inner) {
        inner.setAttribute('rx', (orx * 70).toFixed(2));
        inner.setAttribute('ry', (ory * 68).toFixed(2));
      }
    }
    position(angle) {
      return { x: this.cx + this.rx * Math.cos(angle), y: this.cy + this.ry * Math.sin(angle), depth: (Math.sin(angle) + 1) / 2 };
    }
    draw() {
      const n = this.nodes.length;
      const step = (Math.PI * 2) / n;
      const minScale = this.desktop ? 0.62 : 0.72;
      this.nodes.forEach((node, i) => {
        const p = this.position(this.theta + i * step + Math.PI / 2);
        const scale = minScale + (1 - minScale) * p.depth;
        node.style.transform = `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, 0)`;
        node.style.setProperty('--s', scale.toFixed(3));
        node.style.zIndex = p.depth > 0.55 ? 4 : 2;
        node.style.opacity = (0.7 + 0.3 * p.depth).toFixed(3);
      });
      if (this.sat) {
        const p = this.position(this.theta * 2.4 - Math.PI / 5);
        this.sat.style.transform = `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, 0)`;
        this.sat.style.zIndex = p.depth > 0.55 ? 5 : 1;
      }
    }
    frame(now) {
      const dt = this.last ? Math.min(now - this.last, 64) : 0;
      this.last = now;
      const running = this.wantsMotion && motionAllowed() && !this.hoverPause && !this.userPause && !this.dragging && this.visible && !document.hidden;
      if (running) {
        this.theta += (dt / 1000) * ((Math.PI * 2) / this.speed);
        this.draw();
      }
      this.raf = requestAnimationFrame(this.frame);
    }
    setupDrag() {
      let startX = 0;
      let startTheta = 0;
      let moved = false;
      let pointerId = null;
      this.stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startTheta = this.theta;
        moved = false;
      });
      this.stage.addEventListener('pointermove', (event) => {
        if (pointerId !== event.pointerId) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 8) {
          moved = true;
          this.dragging = true;
          try { this.stage.setPointerCapture(event.pointerId); } catch (e) { /* ignore */ }
        }
        if (moved) {
          this.theta = startTheta - dx / Math.max(this.rx, 120);
          this.draw();
        }
      });
      const end = (event) => {
        if (pointerId !== event.pointerId) return;
        pointerId = null;
        this.dragging = false;
        if (moved) {
          this.suppressClick = true;
          setTimeout(() => (this.suppressClick = false), 50);
        }
      };
      this.stage.addEventListener('pointerup', end);
      this.stage.addEventListener('pointercancel', end);
      this.stage.addEventListener(
        'click',
        (event) => {
          if (this.suppressClick) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
    }
  }
  customElements.define('orbit-hero', OrbitHero);

  /* ------------------------------------------------------------------ sliders and tabs */
  class ProductSlider extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-slider]', this);
      this.bar = qs('.slider-progress span', this);
      if (!this.track) return;
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      this.update();
    }
    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = Math.max(scrollWidth - clientWidth, 0);
      const visible = scrollWidth ? Math.min(clientWidth / scrollWidth, 1) : 1;
      if (this.bar) {
        const widthPct = visible * 100;
        const ratio = max ? scrollLeft / max : 0;
        this.bar.style.setProperty('--progress', `${widthPct}%`);
        this.bar.style.setProperty('--offset', `${ratio * ((100 - widthPct) / widthPct) * 100}%`);
        this.bar.parentElement.hidden = max < 4;
      }
      const root = this.closest('.section, product-tabs, product-recommendations');
      if (root && this.offsetParent !== null) {
        const prev = qs('[data-slider-prev]', root);
        const next = qs('[data-slider-next]', root);
        if (prev) prev.disabled = scrollLeft < 4;
        if (next) next.disabled = scrollLeft > max - 4;
      }
    }
    go(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior: motionAllowed() ? 'smooth' : 'auto' });
    }
  }
  customElements.define('product-slider', ProductSlider);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-slider-prev], [data-slider-next]');
    if (!button) return;
    const root = button.closest('.section, product-tabs, product-recommendations');
    if (!root) return;
    const slider = qsa('product-slider', root).find((el) => el.offsetParent !== null);
    if (slider) slider.go(button.hasAttribute('data-slider-next') ? 1 : -1);
  });

  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = qsa('[role="tab"]', this);
      this.viewAll = qs('[data-view-all]', this);
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      const url = this.tabs[index].dataset.url;
      if (this.viewAll && url) {
        const current = new URL(this.viewAll.href, window.location.origin);
        const next = new URL(url, window.location.origin);
        next.search = current.search;
        this.viewAll.href = next.pathname + next.search;
      }
      qsa('product-slider', this).forEach((slider) => slider.update && slider.update());
    }
  }
  customElements.define('product-tabs', ProductTabs);

  /* ------------------------------------------------------------------ quantity */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      qsa('[data-qty]', this).forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const min = Number(this.input.min || 0);
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const step = Number(this.input.step || 1);
          const value = Number(this.input.value || 0) + (button.dataset.qty === 'plus' ? step : -step);
          this.input.value = Math.min(Math.max(value, min), max);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.syncButtons();
        });
      });
      this.input.addEventListener('change', () => this.syncButtons());
      this.syncButtons();
    }
    syncButtons() {
      const minus = qs('[data-qty="minus"]', this);
      const min = Number(this.input.min || 0);
      if (minus) minus.disabled = Number(this.input.value) <= min && min > 0;
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------ product logic */
  function getProductData(scope) {
    if (scope._product) return scope._product;
    const script = qs('[data-product-json]', scope);
    scope._product = script ? JSON.parse(script.textContent) : null;
    return scope._product;
  }

  function priceMarkup(variant, large) {
    if (!variant) return '';
    const rule = (window.theme && window.theme.discount) || { min: 10, step: 5 };
    const pct = variant.compare_at_price > variant.price ? Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
    const onSale = pct > 0 && pct >= rule.min;
    let html = `<div class="price${onSale ? ' price--sale' : ''}${large ? ' price--large' : ''}" data-price>`;
    html += `<span class="price__now">${formatMoney(variant.price)}</span>`;
    if (onSale) {
      const save = Math.floor(pct / rule.step) * rule.step;
      html += `<s class="price__was">${formatMoney(variant.compare_at_price)}</s>`;
      if (large && strings.save) html += `<span class="price__save">${strings.save.replace('[percent]', save)}</span>`;
    }
    html += '</div>';
    return html;
  }

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest('[data-product-scope]');
      if (!this.scope) return;
      this.product = getProductData(this.scope);
      if (!this.product) return;
      this.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) this.onChange();
      });
      this.refreshAvailability();
    }
    selectedOptions() {
      const count = this.product.options.length;
      const values = [];
      for (let position = 1; position <= count; position += 1) {
        const checked = qs(`input[type="radio"][data-option-position="${position}"]:checked`, this);
        const hidden = qs(`input[type="hidden"][data-option-position="${position}"]`, this);
        values.push(checked ? checked.value : hidden ? hidden.value : null);
      }
      return values;
    }
    findVariant(values) {
      return this.product.variants.find((variant) => variant.options.every((option, i) => option === values[i]));
    }
    onChange() {
      const values = this.selectedOptions();
      qsa('[data-option-group]', this).forEach((group) => {
        const position = Number(group.dataset.optionGroup);
        const label = qs('[data-selected-value]', group);
        if (label) label.textContent = values[position - 1] || '';
      });
      const variant = this.findVariant(values);
      this.refreshAvailability();
      this.scope.dispatchEvent(new CustomEvent('variant:change', { detail: { variant }, bubbles: false }));
    }
    refreshAvailability() {
      const values = this.selectedOptions();
      qsa('input[type="radio"][data-option-position]', this).forEach((input) => {
        const position = Number(input.dataset.optionPosition);
        const candidate = values.slice();
        candidate[position - 1] = input.value;
        const match = this.product.variants.find((variant) => {
          return variant.options.every((option, i) => {
            if (i === position - 1) return option === input.value;
            if (i > position - 1) return true;
            return option === candidate[i];
          });
        });
        const available = this.product.variants.some(
          (variant) => variant.available && variant.options.every((option, i) => (i === position - 1 ? option === input.value : i > position - 1 ? true : option === candidate[i]))
        );
        const pill = input.closest('.pill');
        if (pill) pill.classList.toggle('is-unavailable', !match || !available);
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  function setupProductScope(scope) {
    if (!scope || scope._ready) return;
    scope._ready = true;
    const product = getProductData(scope);
    if (!product) return;
    const isQuick = scope.classList.contains('quick');

    scope.addEventListener('variant:change', (event) => {
      const variant = event.detail.variant;
      const formId = scope.dataset.formId;
      const form = formId ? document.getElementById(formId) : qs('form[data-type="add-to-cart-form"]', scope);
      const input = form ? qs('[data-variant-input]', form) : null;
      const addButton = form ? qs('[data-add-button]', form) : null;
      const addLabel = addButton ? qs('[data-add-label]', addButton) : null;
      const priceContainer = qs('[data-price-container]', scope);
      const error = form ? qs('[data-form-error]', form) : null;
      if (error) error.hidden = true;

      if (input && variant) input.value = variant.id;

      const available = Boolean(variant && variant.available);
      const label = !variant ? strings.unavailable : available ? strings.addToCart : strings.soldOut;
      if (addButton) addButton.disabled = !available;
      if (addLabel) addLabel.textContent = label;

      if (priceContainer && variant) priceContainer.innerHTML = priceMarkup(variant, true);

      if (variant && !isQuick && scope.dataset.url) {
        const url = `${scope.dataset.url}?variant=${variant.id}`;
        window.history.replaceState({ variant: variant.id }, '', url);
      }

      if (variant && variant.featured_media) {
        if (isQuick) {
          const media = qs('[data-quick-media] img', scope);
          const src = variant.featured_media.preview_image && variant.featured_media.preview_image.src;
          if (media && src) {
            media.srcset = '';
            media.src = `${src}${src.includes('?') ? '&' : '?'}width=900`;
          }
        } else {
          const gallery = qs('media-gallery', scope);
          if (gallery) gallery.show(variant.featured_media.id);
        }
      }

      const sticky = qs('sticky-buy', scope);
      if (sticky) {
        const vt = qs('[data-sticky-variant]', sticky);
        if (vt && variant) vt.textContent = variant.title;
        const sp = qs('[data-sticky-price]', sticky);
        if (sp && variant) sp.innerHTML = priceMarkup(variant, false);
        const sb = qs('[data-sticky-add]', sticky);
        if (sb) {
          sb.disabled = !available;
          sb.textContent = label;
        }
      }
    });
  }

  class ProductPage extends HTMLElement {
    connectedCallback() {
      setupProductScope(this);
      qsa('[data-share]', this).forEach((button) => {
        button.addEventListener('click', async () => {
          const url = button.dataset.url;
          if (navigator.share) {
            try { await navigator.share({ url, title: document.title }); } catch (e) { /* cancelled */ }
          } else if (navigator.clipboard) {
            await navigator.clipboard.writeText(url);
            toast(button.dataset.copied);
          }
        });
      });
      qsa('[data-delivery]', this).forEach(renderDelivery);
    }
  }
  customElements.define('product-page', ProductPage);

  function renderDelivery(el) {
    const min = Number(el.dataset.min) || 6;
    const max = Number(el.dataset.max) || 12;
    const cutoff = Number(el.dataset.cutoff);
    const locale = el.dataset.locale || document.documentElement.lang || 'en';
    const addBusinessDays = (date, days) => {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      return result;
    };
    let start = new Date();
    if (!Number.isNaN(cutoff) && start.getHours() >= cutoff) start.setDate(start.getDate() + 1);
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric' });
    const range = qs('[data-delivery-range]', el);
    if (range) range.textContent = ` ${fmt.format(addBusinessDays(start, min))} – ${fmt.format(addBusinessDays(start, max))}`;
    el.hidden = false;
  }

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-gallery-track]', this);
      this.current = qs('[data-gallery-current]', this);
      this.scope = this.closest('[data-product-scope]');
      if (!this.track) return;
      this.track.addEventListener(
        'scroll',
        debounce(() => {
          if (isDesktop()) return;
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (this.current) this.current.textContent = String(index + 1);
        }, 60),
        { passive: true }
      );
      qsa('[data-zoom]', this).forEach((button) => {
        button.addEventListener('click', () => this.openLightbox(Number(button.dataset.zoom), button));
      });
      const lightbox = this.scope ? qs('[data-lightbox]', this.scope) : null;
      if (lightbox) {
        this.lightbox = lightbox;
        qs('[data-close]', lightbox).addEventListener('click', () => this.closeLightbox());
        lightbox.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            this.closeLightbox();
          }
        });
      }
    }
    show(mediaId) {
      const item = qs(`[data-media-id="${mediaId}"]`, this.track);
      if (!item) return;
      if (isDesktop()) {
        if (this.track.firstElementChild !== item) {
          this.track.prepend(item);
        }
      } else {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft - parseFloat(getComputedStyle(this.track).paddingLeft || 0), behavior: motionAllowed() ? 'smooth' : 'auto' });
      }
    }
    openLightbox(index, trigger) {
      if (!this.lightbox) return;
      this.lastTrigger = trigger;
      this.lightbox.classList.add('is-open');
      document.body.classList.add('has-overlay');
      const track = qs('[data-lightbox-track]', this.lightbox);
      const slide = qs(`[data-index="${index}"]`, track);
      requestAnimationFrame(() => {
        if (slide) track.scrollLeft = slide.offsetLeft;
        trapFocus(this.lightbox, qs('[data-close]', this.lightbox));
      });
    }
    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.classList.remove('is-open');
      if (!openStack.length) document.body.classList.remove('has-overlay');
      releaseFocus();
      if (this.lastTrigger) this.lastTrigger.focus({ preventScroll: true });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const scope = this.closest('[data-product-scope]');
      const target = scope ? qs('[data-main-buy]', scope) : null;
      if (!target) return;
      this.io = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.classList.toggle('is-visible', passed);
        this.setAttribute('aria-hidden', String(!passed));
        const button = qs('[data-sticky-add]', this);
        if (button) button.tabIndex = passed ? 0 : -1;
      });
      this.io.observe(target);
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }
  }
  customElements.define('sticky-buy', StickyBuy);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }
    async onSubmit(event) {
      event.preventDefault();
      const submitter = event.submitter && event.submitter.name === 'add' ? event.submitter : qs('[data-add-button]', this.form);
      const error = qs('[data-form-error]', this.form);
      if (submitter && submitter.disabled) return;
      if (error) error.hidden = true;
      const buttons = [qs('[data-add-button]', this.form), submitter].filter(Boolean);
      buttons.forEach((b) => b.classList.add('is-loading'));
      try {
        await cart.add(new FormData(this.form));
        const modal = this.closest('quick-view');
        if (modal) modal.close(false);
        if (theme.cartType === 'drawer' && qs('cart-drawer')) {
          qs('cart-drawer').open();
        } else {
          window.location.href = routes.cart;
        }
      } catch (err) {
        if (error) {
          error.textContent = err.message || strings.error;
          error.hidden = false;
        } else {
          toast(err.message || strings.error);
        }
      } finally {
        buttons.forEach((b) => b.classList.remove('is-loading'));
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* Quick add from product cards and cart recommendations */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-quick-add-form]');
    if (!form) return;
    event.preventDefault();
    const button = qs('button[type="submit"]', form);
    if (button) button.classList.add('is-loading');
    try {
      await cart.add(new FormData(form));
      if (button) {
        button.classList.remove('is-loading');
        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 1600);
      }
      cart.open();
    } catch (err) {
      if (button) button.classList.remove('is-loading');
      toast(err.message || strings.error);
    }
  });

  /* ------------------------------------------------------------------ quick view */
  class QuickView extends HTMLElement {
    connectedCallback() {
      this.panel = qs('.modal__panel', this);
      this.content = qs('[data-quick-view-content]', this);
      qs('[data-close]', this).addEventListener('click', () => this.close());
      this.addEventListener('click', (event) => {
        if (event.target === this) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
        }
      });
    }
    async open(url, trigger) {
      this.trigger = trigger;
      if (trigger) trigger.classList.add('is-loading');
      try {
        const sep = url.includes('?') ? '&' : '?';
        const text = await fetchText(`${url}${sep}section_id=quick-view`);
        const root = qs('.quick', parseHTML(text));
        if (!root) throw new Error('missing');
        this.content.innerHTML = '';
        this.content.appendChild(document.importNode(root, true));
        setupProductScope(qs('.quick', this.content));
        if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
        this.classList.add('is-open');
        document.body.classList.add('has-overlay');
        trapFocus(this.panel);
      } catch (error) {
        window.location.href = url;
      } finally {
        if (trigger) trigger.classList.remove('is-loading');
      }
    }
    close(restoreFocus = true) {
      this.classList.remove('is-open');
      if (!openStack.length) document.body.classList.remove('has-overlay');
      releaseFocus();
      if (restoreFocus && this.trigger) this.trigger.focus({ preventScroll: true });
      setTimeout(() => {
        if (!this.classList.contains('is-open')) this.content.innerHTML = '';
      }, 350);
    }
  }
  customElements.define('quick-view', QuickView);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-quick-view]');
    if (!trigger) return;
    event.preventDefault();
    const modal = qs('quick-view');
    if (modal) modal.open(trigger.dataset.quickView, trigger);
    else window.location.href = trigger.dataset.quickView;
  });

  /* ------------------------------------------------------------------ cart drawer and page */
  function bindCartLines(root, onAfter) {
    const handleChange = debounce(async (input) => {
      const index = Number(input.dataset.lineIndex);
      const line = input.closest('[data-line]');
      if (line) line.classList.add('is-busy');
      try {
        await cart.change(index, Number(input.value));
        if (onAfter) onAfter();
      } catch (err) {
        if (line) {
          line.classList.remove('is-busy');
          const message = qs('[data-line-error]', line);
          if (message) {
            message.textContent = err.message || strings.quantityError;
            message.hidden = false;
          }
        }
      }
    }, 350);
    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-line-index]')) handleChange(event.target);
      if (event.target.matches('[data-cart-note]')) cart.note(event.target.value);
    });
    root.addEventListener('click', async (event) => {
      const remove = event.target.closest('[data-line-remove]');
      if (!remove) return;
      const line = remove.closest('[data-line]');
      if (line) line.classList.add('is-busy');
      try {
        await cart.change(Number(remove.dataset.lineRemove), 0);
        if (onAfter) onAfter();
      } catch (err) {
        if (line) line.classList.remove('is-busy');
        toast(err.message || strings.error);
      }
    });
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.bindPanel();
      bindCartLines(this, () => this.focusPanel());
    }
    bindPanel() {
      this.panel = qs('[data-cart-drawer]', this);
      this.overlay = qs('[data-drawer-overlay]', this);
      if (this.overlay && !this.overlay._bound) {
        this.overlay._bound = true;
        this.overlay.addEventListener('click', () => this.close());
      }
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-close]')) this.close();
      });
    }
    open(trigger) {
      if (!this.panel) return;
      if (this.panel.classList.contains('is-open')) return;
      openDrawer(this.panel, this.overlay, trigger);
      this.loadRecommendations();
    }
    close() {
      closeDrawer(this.panel);
    }
    focusPanel() {
      if (this.panel && this.panel.classList.contains('is-open')) this.panel.focus({ preventScroll: true });
    }
    render(html) {
      const fresh = qs('[data-cart-drawer]', parseHTML(html));
      if (!fresh || !this.panel) return;
      const body = qs('[data-cart-body]', this.panel);
      const scroll = body ? body.scrollTop : 0;
      this.panel.innerHTML = fresh.innerHTML;
      const newBody = qs('[data-cart-body]', this.panel);
      if (newBody) newBody.scrollTop = scroll;
      if (this.panel.classList.contains('is-open')) this.loadRecommendations();
    }
    loadRecommendations() {
      const holder = qs('cart-recommendations', this.panel);
      if (holder && holder.load) holder.load();
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  class CartRecommendations extends HTMLElement {
    async load() {
      if (this._loaded || !this.dataset.url) return;
      this._loaded = true;
      try {
        const text = await fetchText(this.dataset.url);
        const content = qs('[data-recs-content]', parseHTML(text));
        if (content && qs('.mini', content)) {
          this.innerHTML = content.innerHTML;
          this.hidden = false;
        }
      } catch (e) {
        /* recommendations are optional */
      }
    }
  }
  customElements.define('cart-recommendations', CartRecommendations);

  class CartPage extends HTMLElement {
    connectedCallback() {
      bindCartLines(this);
    }
    render(html) {
      const fresh = qs('cart-page', parseHTML(html));
      if (fresh) this.innerHTML = fresh.innerHTML;
    }
  }
  customElements.define('cart-page', CartPage);

  /* ------------------------------------------------------------------ collection filters */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.bind();
      this.onPop = () => this.load(window.location.search.slice(1), false);
      window.addEventListener('popstate', this.onPop);
    }
    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
    }
    bind() {
      this.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-filters-open]');
        if (openButton) {
          openDrawer(qs('[data-filter-drawer]', this), qs('[data-drawer-overlay]', this), openButton);
          return;
        }
        if (event.target.closest('[data-drawer-overlay]')) {
          closeDrawer(qs('[data-filter-drawer]', this));
          return;
        }
        if (event.target.closest('[data-filter-drawer] [data-close]')) {
          closeDrawer(qs('[data-filter-drawer]', this));
          return;
        }
        const link = event.target.closest('[data-facet-link], [data-facet-clear]');
        if (link) {
          event.preventDefault();
          const url = new URL(link.href, window.location.origin);
          this.load(url.searchParams.toString());
          return;
        }
        const more = event.target.closest('[data-load-more]');
        if (more) {
          event.preventDefault();
          this.loadMore(more);
        }
      });
      const onFormChange = debounce(() => this.submit(), 450);
      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-facet-form]')) onFormChange();
        if (event.target.matches('[data-sort]')) this.submit();
      });
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-facet-form] input[type="number"]')) onFormChange();
      });
      this.addEventListener('submit', (event) => {
        if (event.target.matches('[data-facet-form]')) {
          event.preventDefault();
          this.submit();
        }
      });
    }
    params() {
      const form = qs('[data-facet-form]', this);
      const params = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams(window.location.search);
      const sort = qs('[data-sort]', this);
      if (sort) params.set('sort_by', sort.value);
      for (const [key, value] of Array.from(params.entries())) {
        if (value === '') params.delete(key);
      }
      params.delete('page');
      return params.toString();
    }
    submit() {
      this.load(this.params());
    }
    async load(query, push = true) {
      const results = qs('[data-results]', this);
      if (results) results.style.opacity = '0.45';
      const path = window.location.pathname;
      const url = `${path}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
      try {
        const doc = parseHTML(await fetchText(url));
        const fresh = qs('facet-filters', doc);
        if (!fresh) throw new Error('missing');
        this.swap(fresh);
        if (push) window.history.pushState({}, '', `${path}${query ? '?' + query : ''}`);
      } catch (e) {
        window.location.href = `${path}?${query}`;
      } finally {
        const r = qs('[data-results]', this);
        if (r) r.style.opacity = '';
      }
    }
    swap(fresh) {
      const replace = (selector) => {
        const current = qs(selector, this);
        const next = qs(selector, fresh);
        if (current && next) current.innerHTML = next.innerHTML;
      };
      replace('[data-results]');
      replace('.toolbar__left');
      const form = qs('[data-facet-form]', this);
      const freshForm = qs('[data-facet-form]', fresh);
      const active = document.activeElement;
      if (form && freshForm && !(active && form.contains(active) && active.type === 'number')) {
        const openStates = qsa('details', form).map((d) => d.open);
        form.innerHTML = freshForm.innerHTML;
        qsa('details', form).forEach((d, i) => {
          if (typeof openStates[i] === 'boolean') d.open = openStates[i];
        });
      }
      const foot = qs('[data-filter-drawer] .drawer__foot', this);
      const freshFoot = qs('[data-filter-drawer] .drawer__foot', fresh);
      if (foot && freshFoot) foot.innerHTML = freshFoot.innerHTML;
    }
    async loadMore(button) {
      button.classList.add('is-loading');
      try {
        const url = new URL(button.href, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const doc = parseHTML(await fetchText(url.toString()));
        const grid = qs('[data-grid]', this);
        const freshGrid = qs('[data-grid]', doc);
        const firstNew = freshGrid ? freshGrid.firstElementChild : null;
        if (grid && freshGrid) Array.from(freshGrid.children).forEach((card) => grid.appendChild(card));
        const pager = qs('[data-pager]', this);
        const freshPager = qs('[data-pager]', doc);
        if (pager && freshPager) pager.replaceWith(freshPager);
        else if (pager) pager.remove();
        if (firstNew) {
          const link = qs('a', firstNew);
          if (link) link.focus({ preventScroll: true });
        }
      } catch (e) {
        window.location.href = button.href;
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ------------------------------------------------------------------ recommendations */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.children.length) return;
      this.io = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.io.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.io.observe(this);
    }
    async load() {
      try {
        const doc = parseHTML(await fetchText(this.dataset.url));
        const fresh = qs('product-recommendations', doc);
        if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
      } catch (e) {
        /* optional */
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------ misc */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-localization-form] select');
    if (select) select.form.submit();
  });

  document.addEventListener('shopify:section:load', () => {
    document.body.classList.remove('has-overlay');
  });
})();
